import React, { useEffect, useState } from "react";
import { api, RSD } from "../../../lib/api";
import { Card } from "./OrderAnalytics";

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const dt = (s) => (s ? new Date(s).toLocaleString("sr-RS", { dateStyle: "short", timeStyle: "short" }) : "—");
const delta = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v}%`);
const INSUFFICIENT = "Nedovoljno podataka";

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">{title}</p>
      {children}
    </div>
  );
}

function Comparison({ c }) {
  if (!c || c.status !== "READY") {
    return (
      <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400" data-testid="pr-comparison-insufficient">
        {INSUFFICIENT} za uporedbu sa istorijom (potrebno najmanje {c?.requirements?.min_periods || 2} uporediva perioda
        i {c?.requirements?.min_completed_orders || 20} završenih porudžbina).
      </p>
    );
  }
  const rows = [["Završene porudžbine", c.campaign.completed_orders, c.baseline.completed_orders, c.difference_pct.completed_orders, (v) => v],
                ["Promet", c.campaign.completed_revenue, c.baseline.completed_revenue, c.difference_pct.completed_revenue, RSD],
                ["Komada (proizvodi na akciji)", c.campaign.units, c.baseline.units, c.difference_pct.units, (v) => v],
                ["AOV", c.campaign.avg_order_value, c.baseline.avg_order_value, null, RSD]];
  return (
    <div className="bg-white rounded-2xl border divide-y" data-testid="pr-comparison">
      <p className="p-3 text-[11px] text-slate-400">{c.baseline_label} · prosek po periodu</p>
      {rows.map(([label, a, b, d, fmt]) => (
        <div key={label} className="p-3 flex items-baseline justify-between text-sm gap-2">
          <span className="text-slate-500">{label}</span>
          <span className="text-right font-bold">
            {fmt(a)} <span className="text-slate-400 font-normal">vs {fmt(b)}</span>
            {d !== null && <span className={`block text-[11px] ${d > 0 ? "text-brand-green" : d < 0 ? "text-brand-red" : "text-slate-400"}`}>{delta(d)}</span>}
          </span>
        </div>
      ))}
      <p className="p-3 text-[11px] text-slate-400">{c.note}</p>
    </div>
  );
}

function Detail({ c }) {
  const e = c.exposure || {};
  const a = c.acquisition || {};
  return (
    <div className="space-y-3 pt-2" data-testid={`pr-detail-${c.promotion_id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="Doseg kampanje" value={e.available ? e.unique_reach : "—"} sub="jedinstvene sesije" testid="pr-reach" />
        <Card label="Prikazi proizvoda" value={e.available ? e.raw_impressions : "—"} testid="pr-impressions" />
        <Card label="Izložene → porudžbina" value={pct(e.exposure_to_order_conversion)} testid="pr-exposure-conv" />
        <Card label="Prodato komada" value={c.units_sold} testid="pr-units" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="Bruto bazna vrednost" value={RSD(c.base_gross_value)} testid="pr-gross" />
        <Card label="Dat popust" value={RSD(c.discount_given)} testid="pr-discount" />
        <Card label="Finalni promet" value={RSD(c.completed_revenue)} testid="pr-revenue" />
        <Card label="AOV" value={RSD(c.avg_order_value)} testid="pr-aov" />
      </div>
      {e.available && <p className="text-[11px] text-slate-400">{e.note}</p>}

      <Section title="Kupci">
        <div className="bg-white rounded-2xl border divide-y text-sm" data-testid="pr-acquisition">
          <div className="p-3 flex justify-between"><span className="text-slate-500">Novi na Ponesi</span><b>{a.new_to_ponesi ?? 0}</b></div>
          <div className="p-3 flex justify-between"><span className="text-slate-500">Vraćeni Ponesi kupci</span><b>{a.returning_to_ponesi ?? 0}</b></div>
          <div className="p-3 flex justify-between"><span className="text-slate-500">Prvi put u ovom restoranu</span><b>{a.new_to_restaurant ?? 0}</b></div>
          <div className="p-3 flex justify-between"><span className="text-slate-500">Vraćeni gosti restorana</span><b>{a.returning_to_restaurant ?? 0}</b></div>
        </div>
      </Section>

      {c.product_performance && (
        <Section title="Promovisani proizvod — pre vs tokom">
          {!c.product_performance.comparable ? (
            <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400" data-testid="pr-before-during-insufficient">
              {INSUFFICIENT} za uporedbu sa periodom pre akcije. Tokom akcije: {c.product_performance.during.units} kom · {RSD(c.product_performance.during.revenue)}
            </p>
          ) : (
            <div className="bg-white rounded-2xl border divide-y text-sm" data-testid="pr-before-during">
              <div className="p-3 flex justify-between"><span className="text-slate-500">Komada tokom akcije</span><b>{c.product_performance.during.units}</b></div>
              <div className="p-3 flex justify-between"><span className="text-slate-500">Komada pre akcije</span><b>{c.product_performance.before.units}</b></div>
              <div className="p-3 flex justify-between"><span className="text-slate-500">Promet tokom akcije</span><b>{RSD(c.product_performance.during.revenue)}</b></div>
              <div className="p-3 flex justify-between"><span className="text-slate-500">Promet pre akcije</span><b>{RSD(c.product_performance.before.revenue)}</b></div>
              <p className="p-3 text-[11px] text-slate-400">{c.product_performance.baseline_label} · promena komada {delta(c.product_performance.units_change_pct)}</p>
            </div>
          )}
        </Section>
      )}

      {c.store_wide_value && (
        <Section title="Popust na sve — vrednost">
          <div className="bg-white rounded-2xl border divide-y text-sm" data-testid="pr-store-value">
            <div className="p-3 flex justify-between"><span className="text-slate-500">Originalna vrednost proizvoda</span><b>{RSD(c.store_wide_value.original_product_value)}</b></div>
            <div className="p-3 flex justify-between"><span className="text-slate-500">Dat popust</span><b>−{RSD(c.store_wide_value.discount_amount)}</b></div>
            <div className="p-3 flex justify-between"><span className="text-slate-500">Finalni prihod od tih proizvoda</span><b>{RSD(c.store_wide_value.final_revenue)}</b></div>
          </div>
        </Section>
      )}

      <Section title="Uporedba sa istorijom"><Comparison c={c.comparison} /></Section>
    </div>
  );
}

export default function PromotionsTab({ rid }) {
  const [d, setD] = useState(null);
  const [recs, setRecs] = useState(null);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/restaurant/${rid}/promotions/analytics`).then(({ data }) => setD(data)).catch(() => setErr(true));
    api.get(`/restaurant/${rid}/promotions/recommendations`).then(({ data }) => setRecs(data)).catch(() => {});
  }, [rid]);

  if (err) return <p className="text-brand-red text-sm" data-testid="pr-error">Greška pri učitavanju analitike promocija.</p>;
  if (!d) return <p className="text-slate-400 text-sm" data-testid="pr-loading">Učitavanje…</p>;

  const t = d.totals_by_type;
  return (
    <div className="space-y-4" data-testid="ra-promotions">
      <div className="grid grid-cols-3 gap-2">
        {[["HAPPY_HOUR", "Happy Hour"], ["RESTAURANT_WIDE_DISCOUNT", "Popust na sve"], ["PRODUCT_DISCOUNT", "Popust na proizvod"]].map(([k, l]) => (
          <Card key={k} label={l} value={RSD(t[k].completed_revenue)}
            sub={`${t[k].campaigns} kampanja · ${t[k].completed_orders} porudžbina · −${RSD(t[k].discount_given)}`}
            testid={`pr-total-${k}`} />
        ))}
      </div>

      <Section title="Kampanje">
        {!d.campaigns.length ? (
          <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400" data-testid="pr-empty">Još nemate ni jednu promociju.</p>
        ) : (
          <div className="bg-white rounded-2xl border divide-y" data-testid="pr-campaigns">
            {d.campaigns.map((c) => (
              <div key={c.promotion_id} className="p-3">
                <button data-testid={`pr-campaign-${c.promotion_id}`}
                  onClick={() => setOpen(open === c.promotion_id ? null : c.promotion_id)}
                  className="w-full text-left">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-bold truncate">{c.name}</span>
                    <span className="shrink-0 text-slate-500">{c.completed_orders} · {RSD(c.completed_revenue)}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {c.type_label} · {c.status} · {c.promotion_type === "HAPPY_HOUR"
                      ? `${c.start_time}–${c.end_time} · ${(c.days || []).join(", ")}`
                      : `${dt(c.starts_at)} → ${c.ends_at ? dt(c.ends_at) : "bez kraja"}`}
                    {" · "}{c.all_products ? "svi proizvodi" : `${c.products_affected} proizvod`}
                    {c.discount_value ? ` · −${c.discount_value}${c.discount_type === "PERCENT" || c.discount_type === "PERCENTAGE" ? "%" : " RSD"}` : ""}
                  </p>
                </button>
                {open === c.promotion_id && <Detail c={c} />}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Preporuke za Happy Hour">
        {!recs ? <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400">Učitavanje…</p>
          : !recs.enough_data ? (
            <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400" data-testid="pr-recs-insufficient">
              {recs.message || `${INSUFFICIENT} za preporuku.`}
            </p>
          ) : !recs.items.length ? (
            <p className="bg-white rounded-2xl border p-3 text-sm text-slate-500" data-testid="pr-recs-none">{recs.message || "Trenutno nema perioda sa značajno nižom prodajom."}</p>
          ) : (
            <div className="space-y-2" data-testid="pr-recs">
              {recs.items.map((r) => (
                <div key={r.product_id} className="bg-white rounded-2xl border p-3 text-sm">
                  <p className="text-slate-700">{r.reason}</p>
                  <p className="font-bold mt-1">Predlog: {r.product_name} · {r.day_label ? `${r.day_label} · ` : ""}{r.start_time}–{r.end_time} · −{r.discount_value}%</p>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">{recs.note}</p>
            </div>
          )}
      </Section>

      <p className="text-[11px] text-slate-400" data-testid="pr-note">{d.note}</p>
    </div>
  );
}
